/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useMemo, useState } from 'react';
import InputMask from 'react-input-mask';
import Select from 'react-select';
import { cleanCnpj, validateCnpj } from 'utils/cnpj';
import { validateCpf } from 'utils/cpf';
import { countryCodeSafeMaskProps, isCelularValid } from 'utils/phone';
import {
  CNPJ_MASK,
  EMAIL_REGEX,
  MAX_LIST_ROWS,
  inputClass,
  isFullName,
  nextRowId,
  selectStyles,
} from '../helpers';
import { RELATION_OPTIONS } from '../options';
import { TechnicalContactRelation, TechnicalLegalValues, YesNoChoice } from '../types';

type FieldName = Exclude<keyof TechnicalLegalValues, 'rep_legal_outras_empresas'>;
type TextFieldName = Exclude<FieldName, 'tecnico_vinculo' | 'rep_legal_tem_outras_empresas'>;
type OtherCompanyField = 'nome' | 'cnpj';

interface TechnicalLegalErrors {
  fields: Partial<Record<FieldName, string>>;
  otherCompanies: Record<number, Partial<Record<OtherCompanyField, string>>>;
}

interface TechnicalLegalStepProps {
  values: TechnicalLegalValues;
  onChange: (values: TechnicalLegalValues) => void;
  onNext: () => void;
  onBack: () => void;
  onReturnToReview: (() => void) | undefined;
}

const validate = (values: TechnicalLegalValues): TechnicalLegalErrors => {
  const fields: TechnicalLegalErrors['fields'] = {};
  const otherCompanies: TechnicalLegalErrors['otherCompanies'] = {};

  if (!isFullName(values.tecnico_nome)) {
    fields.tecnico_nome = 'Informe o nome completo do responsável técnico.';
  }

  if (!EMAIL_REGEX.test(values.tecnico_email.trim())) {
    fields.tecnico_email = 'Informe um e-mail válido.';
  }

  if (!isCelularValid(values.tecnico_telefone)) {
    fields.tecnico_telefone = 'Informe o celular com DDD.';
  }

  if (!values.tecnico_vinculo) {
    fields.tecnico_vinculo = 'Selecione uma opção.';
  }

  if (!isFullName(values.rep_legal_nome)) {
    fields.rep_legal_nome = 'Informe o nome completo do representante legal.';
  }

  if (!validateCpf(values.rep_legal_cpf)) {
    fields.rep_legal_cpf = 'Informe um CPF válido.';
  }

  if (!values.rep_legal_tem_outras_empresas) {
    fields.rep_legal_tem_outras_empresas = 'Selecione uma opção.';
  } else if (values.rep_legal_tem_outras_empresas === 'sim') {
    if (values.rep_legal_outras_empresas.length === 0) {
      fields.rep_legal_tem_outras_empresas = 'Adicione pelo menos uma empresa ou marque "Não".';
    }

    const seen: string[] = [];

    values.rep_legal_outras_empresas.forEach(({ id, nome, cnpj }) => {
      const rowErrors: Partial<Record<OtherCompanyField, string>> = {};
      const clean = cleanCnpj(cnpj);

      if (nome.trim().length < 2) {
        rowErrors.nome = 'Informe o nome da empresa.';
      }

      if (!clean) {
        rowErrors.cnpj = 'Informe o CNPJ.';
      } else if (!validateCnpj(clean)) {
        rowErrors.cnpj = 'Informe um CNPJ válido.';
      } else if (seen.includes(clean)) {
        rowErrors.cnpj = 'CNPJ repetido.';
      }

      seen.push(clean);

      if (rowErrors.nome || rowErrors.cnpj) {
        otherCompanies[id] = rowErrors;
      }
    });
  }

  return { fields, otherCompanies };
};

const hasErrors = (errors: TechnicalLegalErrors) =>
  Object.keys(errors.fields).length > 0 || Object.keys(errors.otherCompanies).length > 0;

export const isTechnicalLegalComplete = (values: TechnicalLegalValues) =>
  !hasErrors(validate(values));

const TechnicalLegalStep: React.FC<TechnicalLegalStepProps> = ({
  values,
  onChange,
  onNext,
  onBack,
  onReturnToReview,
}) => {
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitted, setSubmitted] = useState<boolean>(false);

  const errors = useMemo(() => validate(values), [values]);
  const isValid = !hasErrors(errors);

  const touch = (key: string) => setTouched((prev) => ({ ...prev, [key]: true }));

  const fieldError = (field: FieldName) =>
    submitted || touched[field] ? errors.fields[field] : undefined;

  const otherCompanyError = (id: number, field: OtherCompanyField) =>
    submitted || touched[`outras_empresas.${id}.${field}`]
      ? errors.otherCompanies[id]?.[field]
      : undefined;

  const handleChange = (field: TextFieldName, value: string) =>
    onChange({ ...values, [field]: value });

  // Com "Não" as linhas ficam guardadas (caso volte ao "Sim"); a lista só vale com "Sim".
  const handleOtherCompaniesChoice = (choice: YesNoChoice) => {
    const rows =
      choice === 'sim' && values.rep_legal_outras_empresas.length === 0
        ? [{ id: 1, nome: '', cnpj: '' }]
        : values.rep_legal_outras_empresas;

    onChange({ ...values, rep_legal_tem_outras_empresas: choice, rep_legal_outras_empresas: rows });
  };

  const addOtherCompany = () =>
    onChange({
      ...values,
      rep_legal_outras_empresas: [
        ...values.rep_legal_outras_empresas,
        { id: nextRowId(values.rep_legal_outras_empresas), nome: '', cnpj: '' },
      ],
    });

  const updateOtherCompany = (id: number, field: OtherCompanyField, value: string) =>
    onChange({
      ...values,
      rep_legal_outras_empresas: values.rep_legal_outras_empresas.map((row) =>
        row.id === id ? { ...row, [field]: value } : row,
      ),
    });

  const removeOtherCompany = (id: number) => {
    setTouched((prev) => {
      const next = { ...prev };
      delete next[`outras_empresas.${id}.nome`];
      delete next[`outras_empresas.${id}.cnpj`];
      return next;
    });

    onChange({
      ...values,
      rep_legal_outras_empresas: values.rep_legal_outras_empresas.filter((row) => row.id !== id),
    });
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitted(true);

    if (isValid) onNext();
  };

  const handleReturnToReview = () => {
    setSubmitted(true);

    if (isValid && onReturnToReview) onReturnToReview();
  };

  const technicalNameError = fieldError('tecnico_nome');
  const technicalEmailError = fieldError('tecnico_email');
  const technicalPhoneError = fieldError('tecnico_telefone');
  const relationError = fieldError('tecnico_vinculo');
  const legalNameError = fieldError('rep_legal_nome');
  const cpfError = fieldError('rep_legal_cpf');
  const otherCompaniesChoiceError = fieldError('rep_legal_tem_outras_empresas');

  const selectedRelation =
    RELATION_OPTIONS.find((option) => option.value === values.tecnico_vinculo) || null;

  return (
    <form noValidate onSubmit={handleSubmit}>
      <p className="card-text font-weight-bold mt-4 mb-1">Responsável técnico</p>
      <p className="text-muted small mb-3">
        Quem mantém a integração funcionando e será procurado se ela parar.
      </p>
      <div className="row">
        <div className="col-md-6 mb-4">
          <label htmlFor="api_request_tecnico_nome">Nome completo</label>
          <input
            id="api_request_tecnico_nome"
            type="text"
            className={inputClass(technicalNameError)}
            value={values.tecnico_nome}
            onChange={(e) => handleChange('tecnico_nome', e.target.value)}
            onBlur={() => touch('tecnico_nome')}
          />
          {technicalNameError && <div className="invalid-feedback">{technicalNameError}</div>}
        </div>
        <div className="col-md-6 mb-4">
          <label htmlFor="api_request_tecnico_email">E-mail</label>
          <input
            id="api_request_tecnico_email"
            type="email"
            className={inputClass(technicalEmailError)}
            value={values.tecnico_email}
            onChange={(e) => handleChange('tecnico_email', e.target.value)}
            onBlur={() => touch('tecnico_email')}
          />
          {technicalEmailError && <div className="invalid-feedback">{technicalEmailError}</div>}
        </div>
      </div>
      <div className="row">
        <div className="col-md-6 mb-4">
          <label htmlFor="api_request_tecnico_telefone">Celular</label>
          <InputMask
            {...countryCodeSafeMaskProps}
            id="api_request_tecnico_telefone"
            type="tel"
            mask="(99) 99999-9999"
            className={inputClass(technicalPhoneError)}
            value={values.tecnico_telefone}
            onChange={(e) => handleChange('tecnico_telefone', e.target.value)}
            onBlur={() => touch('tecnico_telefone')}
          />
          {technicalPhoneError && <div className="invalid-feedback">{technicalPhoneError}</div>}
        </div>
        <div className="col-md-6 mb-4">
          <label htmlFor="api_request_tecnico_vinculo">Vínculo com a empresa</label>
          <Select
            inputId="api_request_tecnico_vinculo"
            options={RELATION_OPTIONS}
            value={selectedRelation}
            onChange={(option) =>
              onChange({
                ...values,
                tecnico_vinculo: (option ? option.value : '') as TechnicalContactRelation,
              })
            }
            onBlur={() => touch('tecnico_vinculo')}
            placeholder="Selecionar"
            styles={selectStyles(Boolean(relationError))}
          />
          {relationError && <div className="invalid-feedback d-block">{relationError}</div>}
        </div>
      </div>

      <p className="card-text font-weight-bold mt-4 mb-1">Representante legal</p>
      <p className="text-muted small mb-3">Quem assina o contrato e as políticas de uso da API.</p>
      <div className="row">
        <div className="col-md-7 mb-4">
          <label htmlFor="api_request_rep_legal_nome">Nome completo</label>
          <input
            id="api_request_rep_legal_nome"
            type="text"
            className={inputClass(legalNameError)}
            value={values.rep_legal_nome}
            onChange={(e) => handleChange('rep_legal_nome', e.target.value)}
            onBlur={() => touch('rep_legal_nome')}
          />
          {legalNameError && <div className="invalid-feedback">{legalNameError}</div>}
        </div>
        <div className="col-md-5 mb-4">
          <label htmlFor="api_request_rep_legal_cpf">CPF</label>
          <InputMask
            id="api_request_rep_legal_cpf"
            type="text"
            inputMode="numeric"
            mask="999.999.999-99"
            className={inputClass(cpfError)}
            value={values.rep_legal_cpf}
            onChange={(e) => handleChange('rep_legal_cpf', e.target.value)}
            onBlur={() => touch('rep_legal_cpf')}
          />
          {cpfError && <div className="invalid-feedback">{cpfError}</div>}
        </div>
      </div>

      <fieldset className="mb-4">
        <legend className="col-form-label pt-0">
          O representante legal trabalha em outras empresas do mercado de licitação?
        </legend>
        <div className="form-check form-check-inline">
          <input
            id="api_request_rep_outras_empresas_sim"
            type="radio"
            name="api_request_rep_tem_outras_empresas"
            className={`form-check-input ${otherCompaniesChoiceError ? 'is-invalid' : ''}`}
            checked={values.rep_legal_tem_outras_empresas === 'sim'}
            onChange={() => handleOtherCompaniesChoice('sim')}
          />
          <label className="form-check-label" htmlFor="api_request_rep_outras_empresas_sim">
            Sim
          </label>
        </div>
        <div className="form-check form-check-inline">
          <input
            id="api_request_rep_outras_empresas_nao"
            type="radio"
            name="api_request_rep_tem_outras_empresas"
            className={`form-check-input ${otherCompaniesChoiceError ? 'is-invalid' : ''}`}
            checked={values.rep_legal_tem_outras_empresas === 'nao'}
            onChange={() => handleOtherCompaniesChoice('nao')}
          />
          <label className="form-check-label" htmlFor="api_request_rep_outras_empresas_nao">
            Não
          </label>
        </div>
        {otherCompaniesChoiceError && (
          <div className="invalid-feedback d-block">{otherCompaniesChoiceError}</div>
        )}

        {values.rep_legal_tem_outras_empresas === 'sim' && (
          <div className="mt-3">
            {values.rep_legal_outras_empresas.map((row, index) => {
              const nameError = otherCompanyError(row.id, 'nome');
              const cnpjError = otherCompanyError(row.id, 'cnpj');
              const inputId = `api_request_rep_outra_empresa_${row.id}`;

              return (
                <div key={row.id} className="row">
                  <div className="col-12 col-md-6 mb-3">
                    <label className="sr-only" htmlFor={`${inputId}_nome`}>
                      Nome da empresa {index + 1}
                    </label>
                    <input
                      id={`${inputId}_nome`}
                      type="text"
                      placeholder="Nome da empresa"
                      className={inputClass(nameError)}
                      value={row.nome}
                      onChange={(e) => updateOtherCompany(row.id, 'nome', e.target.value)}
                      onBlur={() => touch(`outras_empresas.${row.id}.nome`)}
                    />
                    {nameError && <div className="invalid-feedback">{nameError}</div>}
                  </div>
                  <div className="col-10 col-md-5 mb-3">
                    <label className="sr-only" htmlFor={`${inputId}_cnpj`}>
                      CNPJ da empresa {index + 1}
                    </label>
                    <InputMask
                      id={`${inputId}_cnpj`}
                      type="text"
                      mask={CNPJ_MASK}
                      placeholder="CNPJ"
                      className={inputClass(cnpjError)}
                      value={row.cnpj}
                      onChange={(e) =>
                        updateOtherCompany(row.id, 'cnpj', e.target.value.toUpperCase())
                      }
                      onBlur={() => touch(`outras_empresas.${row.id}.cnpj`)}
                    />
                    {cnpjError && <div className="invalid-feedback">{cnpjError}</div>}
                  </div>
                  <div className="col-2 col-md-1 mb-3">
                    <button
                      type="button"
                      className="btn btn-link text-secondary px-0"
                      aria-label={`Remover empresa ${index + 1}`}
                      onClick={() => removeOtherCompany(row.id)}
                    >
                      <span className="material-icons" aria-hidden="true">
                        close
                      </span>
                    </button>
                  </div>
                </div>
              );
            })}
            {values.rep_legal_outras_empresas.length < MAX_LIST_ROWS && (
              <div className="text-right">
                <button type="button" className="btn btn-link" onClick={addOtherCompany}>
                  + adicionar empresa
                </button>
              </div>
            )}
          </div>
        )}
      </fieldset>

      {submitted && !isValid && (
        <div className="alert alert-danger text-center py-2" role="alert">
          Revise os campos destacados para continuar.
        </div>
      )}

      <div className="row mt-2">
        <div className="col-md-12">
          <button type="submit" className="btn btn-lg btn-block btn-submit">
            Continuar
          </button>
          <div className="text-center mt-2">
            {onReturnToReview && (
              <button type="button" className="btn btn-link" onClick={handleReturnToReview}>
                Voltar para a revisão
              </button>
            )}
            <button type="button" className="btn btn-link" onClick={onBack}>
              Voltar
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default TechnicalLegalStep;
